import React, { useEffect, startTransition } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { Frame } from "@shopify/polaris";
import "@shopify/polaris/build/esm/styles.css";
import { store } from "./store/store";
import Home from "./pages/Home";
import SettingsShell from "./pages/settings/SettingsShell";
import SettingsListPage from "./pages/settings/SettingsListPage";
import TemplateEditorPage from "./pages/settings/TemplateEditorPage";
import PlansPage from "./pages/PlansPage";
import AppBridgeErrorBoundary from "./components/AppBridgeErrorBoundary";
import AppBridgeProvider from "./components/AppBridgeProvider";
import NavigationMenuComponent from "./components/NavigationMenu";
import AppFooter from "./components/AppFooter";
import WhatsAppSupportFab from "./components/WhatsAppSupportFab";
import { ROUTER_BASENAME } from "./config/env";

/** Old bookmarks: /app → home with same query string */
function LegacyAppPathRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    startTransition(() => {
      navigate({ pathname: "/", search: location.search }, { replace: true });
    });
  }, [navigate, location.search]);

  return null;
}

function AppContent() {
  /** Dashboard at "/" — Settings and Plans in App Bridge sidebar */
  const navigationLinks = [
    { label: "Settings", destination: "/settings" },
    { label: "Plans & Billing", destination: "/plans" },
  ];

  const matcher = (link, currentLocation) => {
    const path = currentLocation.pathname;
    if (link.destination === "/settings") {
      return path === "/settings" || path.startsWith("/settings/");
    }
    if (link.destination === "/plans") {
      return path === "/plans";
    }
    return false;
  };

  return (
    <AppBridgeProvider>
      <AppBridgeErrorBoundary>
        <Frame>
          <NavigationMenuComponent navigationLinks={navigationLinks} matcher={matcher} />
          <div
            className="age-app-routes-root"
            style={{
              minHeight: "100%",
              minWidth: "100%",
              backgroundColor: "var(--p-color-bg, #f6f6f7)",
              boxSizing: "border-box",
            }}
          >
            <div className="age-app-main">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/plans" element={<PlansPage />} />
                <Route path="/settings" element={<SettingsShell />}>
                  <Route index element={<SettingsListPage />} />
                  <Route path="templates/:templateId" element={<TemplateEditorPage />} />
                </Route>
                <Route path="/app" element={<LegacyAppPathRedirect />} />
                <Route path="/app/settings" element={<Navigate to="/settings" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
            <AppFooter />
          </div>
          <WhatsAppSupportFab />
        </Frame>
      </AppBridgeErrorBoundary>
    </AppBridgeProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <BrowserRouter basename={ROUTER_BASENAME || undefined}>
        <AppContent />
      </BrowserRouter>
    </Provider>
  );
}

export default App;
